import type { CreateWebsiteCategoryPageRequest, WebsiteCategoryPageDetail } from '@utazzo/shared';
import type { DefaultValues } from 'react-hook-form';

const NULLABLE_TEXT_KEYS = [
  'heroHeading',
  'heroSubheading',
  'aboutHtml',
  'metaTitle',
  'metaDescription',
  'metaKeywords',
] as const;

export const CREATE_DEFAULTS: DefaultValues<CreateWebsiteCategoryPageRequest> = {
  slug: '',
  title: '',
  heroHeading: '',
  heroSubheading: '',
  heroMediaId: null,
  heroVideoId: null,
  aboutHtml: '',
  metaTitle: '',
  metaDescription: '',
  metaKeywords: '',
  ogImageId: null,
  status: 'DRAFT',
  bestSellers: [],
  circleDestinations: [],
  destinationOrder: [],
  faqs: [],
  seoLinks: [],
  reviews: [],
};

export type DetailTab =
  | 'details'
  | 'about'
  | 'bestSellers'
  | 'circleDestinations'
  | 'faq'
  | 'seoLinks'
  | 'reviews'
  | 'seo'
  | 'sorting';

const TAB_ORDER: DetailTab[] = [
  'details',
  'about',
  'bestSellers',
  'circleDestinations',
  'faq',
  'seoLinks',
  'reviews',
  'seo',
  'sorting',
];

export const TAB_LABEL: Record<DetailTab, string> = {
  details: 'Details',
  about: 'About',
  bestSellers: 'Best sellers',
  circleDestinations: 'Destination circles',
  faq: 'FAQ',
  seoLinks: 'SEO links',
  reviews: 'Reviews',
  seo: 'SEO',
  sorting: 'Sorting',
};

const FIELD_TAB: Record<string, DetailTab> = {
  title: 'details',
  slug: 'details',
  status: 'details',
  heroHeading: 'details',
  heroSubheading: 'details',
  heroMediaId: 'details',
  heroVideoId: 'details',
  aboutHtml: 'about',
  bestSellers: 'bestSellers',
  circleDestinations: 'circleDestinations',
  destinationOrder: 'sorting',
  faqs: 'faq',
  seoLinks: 'seoLinks',
  reviews: 'reviews',
  metaTitle: 'seo',
  metaDescription: 'seo',
  metaKeywords: 'seo',
  ogImageId: 'seo',
};

export const firstErrorTab = (errorKeys: string[]): DetailTab => {
  const tabs = errorKeys.map((k) => FIELD_TAB[k]).filter((t): t is DetailTab => Boolean(t));
  return TAB_ORDER.find((t) => tabs.includes(t)) ?? 'details';
};

export const toForm = (d: WebsiteCategoryPageDetail): CreateWebsiteCategoryPageRequest => ({
  slug: d.slug,
  title: d.title,
  heroHeading: d.heroHeading ?? '',
  heroSubheading: d.heroSubheading ?? '',
  heroMediaId: d.heroMediaId,
  heroVideoId: d.heroVideoId,
  aboutHtml: d.aboutHtml ?? '',
  metaTitle: d.metaTitle ?? '',
  metaDescription: d.metaDescription ?? '',
  metaKeywords: d.metaKeywords ?? '',
  ogImageId: d.ogImageId,
  status: d.status === 'ARCHIVED' ? 'ARCHIVED' : d.status === 'PUBLISHED' ? 'PUBLISHED' : 'DRAFT',
  bestSellers: d.bestSellers.map((b) => ({ packageId: b.packageId })),
  circleDestinations: d.circleDestinations.map((c) => ({
    destinationPageId: c.destinationPageId,
  })),
  destinationOrder: d.destinationOrder.map((o) => ({
    destinationPageId: o.destinationPageId,
  })),
  faqs: d.faqs.map((f) => ({ question: f.question, answerHtml: f.answerHtml })),
  seoLinks: d.seoLinks.map((l) => ({ groupLabel: l.groupLabel, label: l.label, href: l.href })),
  reviews: d.reviews.map((rv) => ({
    customerName: rv.customerName,
    summary: rv.summary,
    rating: rv.rating,
    portraitMediaId: rv.portraitMediaId,
    imageMediaIds: rv.imageMediaIds ?? [],
  })),
});

export const toPayload = (
  values: CreateWebsiteCategoryPageRequest,
): CreateWebsiteCategoryPageRequest => {
  const out: CreateWebsiteCategoryPageRequest = {
    ...values,
    reviews: values.reviews.map((rv) => ({
      ...rv,
      imageMediaIds: (rv.imageMediaIds ?? []).filter((id) => Boolean(id?.trim())),
    })),
  };
  for (const key of NULLABLE_TEXT_KEYS) {
    const v = out[key];
    if (typeof v === 'string' && v.trim() === '') out[key] = null;
  }
  return out;
};
